import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FormFeedback } from '../FormFeedback';
import { LoadingSpinner } from '../LoadingSpinner';
import { ParagonEditor } from '../Editor/ParagonEditor';
import {
  getStaffGuide, createGuide, updateGuide,
  getGuideRedirects, createRedirect, deleteRedirect,
  uploadGuideImage, expandGuideImageUrls, normalizeGuideImageUrls,
} from '../../services/GuideService';

function toSlug(value) {
  return value.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

function fmtDateTimeLocal(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function GuideForm({ guideId, defaultCategoryId, categories, onSaved }) {
  const isEdit = !!guideId;

  const [loading,    setLoading]    = useState(isEdit);
  const [loadError,  setLoadError]  = useState(null);

  const [categoryId, setCategoryId] = useState(defaultCategoryId ?? '');
  const [title,      setTitle]      = useState('');
  const [slug,       setSlug]       = useState('');
  const [content,    setContent]    = useState('');
  const [status,     setStatus]     = useState('draft');
  const [publishAt,  setPublishAt]  = useState('');
  const [expiresAt,  setExpiresAt]  = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const [redirects,      setRedirects]      = useState([]);
  const [newRedirectCat, setNewRedirectCat] = useState('');
  const [newRedirectGuide, setNewRedirectGuide] = useState('');
  const [redirectSaving, setRedirectSaving] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);

  // Load guide data when editing
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    async function load() {
      try {
        const [guide, redir] = await Promise.all([
          getStaffGuide(guideId),
          getGuideRedirects(guideId),
        ]);
        if (cancelled) return;
        setCategoryId(guide.CategoryId);
        setTitle(guide.Title);
        setSlug(guide.Slug);
        setContent(expandGuideImageUrls(guide.Content));
        setStatus(guide.Status);
        setPublishAt(fmtDateTimeLocal(guide.PublishAt));
        setExpiresAt(fmtDateTimeLocal(guide.ExpiresAt));
        setSlugTouched(true);
        setRedirects(redir);
      } catch (err) {
        if (!cancelled) setLoadError('Failed to load guide.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [guideId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleTitleChange(e) {
    const val = e.target.value;
    setTitle(val);
    if (!slugTouched) setSlug(toSlug(val));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || !slug.trim() || !categoryId) {
      setError('Title, slug, and category are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        categoryId: Number(categoryId),
        title, slug, content: normalizeGuideImageUrls(content), status,
        publishAt: publishAt ? new Date(publishAt).toISOString() : null,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      };

      let savedId;
      if (isEdit) {
        await updateGuide(guideId, payload);
        savedId = guideId;
      } else {
        savedId = await createGuide({ ...payload, sortOrder: 0 });
      }
      onSaved({
        Id:         Number(savedId),
        CategoryId: payload.categoryId,
        Title:      payload.title,
        Slug:       payload.slug,
        Status:     payload.status,
        PublishAt:  payload.publishAt,
        ExpiresAt:  payload.expiresAt,
      });
    } catch {
      setError('Failed to save guide. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddRedirect(e) {
    e.preventDefault();
    if (!newRedirectCat.trim() || !newRedirectGuide.trim()) return;
    setRedirectSaving(true);
    try {
      const id = await createRedirect(guideId, newRedirectCat.trim(), newRedirectGuide.trim());
      setRedirects(prev => [...prev, {
        Id: Number(id),
        FromCategorySlug: newRedirectCat.trim(),
        FromGuideSlug: newRedirectGuide.trim(),
      }]);
      setNewRedirectCat('');
      setNewRedirectGuide('');
    } catch { /* silently ignore — rare duplicate conflict */ }
    finally { setRedirectSaving(false); }
  }

  async function handleDeleteRedirect(redirectId) {
    try {
      await deleteRedirect(redirectId);
      setRedirects(prev => prev.filter(r => r.Id !== redirectId));
    } catch { /* silently ignore */ }
  }

  async function handleImageUpload(file) {
    return uploadGuideImage(file);
  }

  if (loading) return <LoadingSpinner />;
  if (loadError) return <p className="sgm-load-error">{loadError}</p>;

  return (
    <Form className="modal-form" onSubmit={handleSubmit}>
      <Form.Group className="mb-3" controlId="guide-category">
        <Form.Label>Category</Form.Label>
        <Form.Select
          required
          value={categoryId}
          onChange={e => setCategoryId(e.target.value)}
        >
          <option value="">Select a category…</option>
          {categories.map(c => (
            <option key={c.Id} value={c.Id}>{c.Name}</option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3" controlId="guide-title">
        <Form.Label>Title</Form.Label>
        <Form.Control
          type="text"
          placeholder="Guide title"
          required
          value={title}
          onChange={handleTitleChange}
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="guide-slug">
        <Form.Label>Slug</Form.Label>
        <Form.Control
          type="text"
          placeholder="guide-slug"
          required
          value={slug}
          onChange={e => { setSlug(e.target.value); setSlugTouched(true); }}
        />
      </Form.Group>

      <div className="mb-3">
        <Form.Label>Status</Form.Label>
        <div className="sgm-status-radios">
          {['draft', 'published', 'disabled'].map(s => (
            <Form.Check
              key={s}
              inline
              type="radio"
              id={`status-${s}`}
              label={s.charAt(0).toUpperCase() + s.slice(1)}
              value={s}
              checked={status === s}
              onChange={() => setStatus(s)}
            />
          ))}
        </div>
      </div>

      <div className="sgm-datetime-row mb-3">
        <Form.Group controlId="guide-publish-at">
          <Form.Label>Publish At <small className="text-muted">(optional)</small></Form.Label>
          <Form.Control
            type="datetime-local"
            value={publishAt}
            onChange={e => setPublishAt(e.target.value)}
          />
        </Form.Group>
        <Form.Group controlId="guide-expires-at">
          <Form.Label>Expires At <small className="text-muted">(optional)</small></Form.Label>
          <Form.Control
            type="datetime-local"
            value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)}
          />
        </Form.Group>
      </div>

      <div className="mb-3">
        <Form.Label>Content</Form.Label>
        <ParagonEditor
          key={guideId ?? 'new'}
          value={content}
          onChange={setContent}
          onImageUpload={handleImageUpload}
          placeholder="Write the guide content…"
        />
      </div>

      {/* Redirect management — only available when editing an existing guide */}
      {isEdit && (
        <div className="mb-3 sgm-redirects">
          <Form.Label>Legacy Redirects</Form.Label>
          {redirects.length > 0 && (
            <ul className="sgm-redirect-list">
              {redirects.map(r => (
                <li key={r.Id}>
                  <span className="sgm-redirect-path">/{r.FromCategorySlug}/{r.FromGuideSlug}</span>
                  <button
                    type="button"
                    className="sgm-redirect-del"
                    onClick={() => handleDeleteRedirect(r.Id)}
                  >✕</button>
                </li>
              ))}
            </ul>
          )}
          <div className="sgm-redirect-add">
            <Form.Control
              size="sm"
              placeholder="From category slug"
              value={newRedirectCat}
              onChange={e => setNewRedirectCat(e.target.value)}
            />
            <Form.Control
              size="sm"
              placeholder="From guide slug"
              value={newRedirectGuide}
              onChange={e => setNewRedirectGuide(e.target.value)}
            />
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={handleAddRedirect}
              disabled={redirectSaving || !newRedirectCat.trim() || !newRedirectGuide.trim()}
            >
              Add
            </Button>
          </div>
        </div>
      )}

      <FormFeedback type="error" message={error} />

      <div className="modal-form-actions">
        <Button variant="success" type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Guide')}
        </Button>
      </div>
    </Form>
  );
}
